// ─────────────────────────────────────────
// Passport.js — your golf identity + course history
// ─────────────────────────────────────────
// Design inspired by Strava desktop athlete profile:
//   - Hero banner with large avatar, full name, bio, location
//   - Stats strip: rounds, courses, best score, avg score
//   - Season stats (this year at a glance)
//   - Achievements grid (earned badges from round milestones)
//   - Recent Activity — Strava-style activity cards
//   - Course Passport grid
//   - Photo strip
//   - Connected Platforms

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import EditRoundModal from '../components/EditRoundModal'
import AvatarImg from '../components/AvatarImg'
import Nav from '../components/Nav'
import { useIsMobile } from '../hooks/useIsMobile'

export default function Passport({ session }) {
  const isMobile = useIsMobile()
  const [profile, setProfile] = useState(null)
  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(true)
  const [lightboxUrl, setLightboxUrl] = useState(null)
  const [editingRound, setEditingRound] = useState(null)
  const [showAllRounds, setShowAllRounds] = useState(false)
  const [followerCount, setFollowerCount] = useState(0)
  const [followingCount, setFollowingCount] = useState(0)
  const [savingVisibility, setSavingVisibility] = useState(false)
  const [followModal, setFollowModal] = useState(null) // 'followers' | 'following'
  const [followModalUsers, setFollowModalUsers] = useState([])
  const [followModalLoading, setFollowModalLoading] = useState(false)

  useEffect(() => {
    fetchPassportData()
  }, [])

  async function fetchPassportData() {
    const [{ data: profileData }, { data: roundsData }, { data: followersData }, { data: followingData }] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', session.user.id).single(),
      supabase.from('rounds').select('*').eq('user_id', session.user.id).neq('data_source', 'Post').order('created_at', { ascending: false }),
      supabase.from('follows').select('id').eq('following_id', session.user.id),
      supabase.from('follows').select('id').eq('follower_id', session.user.id),
    ])

    setProfile(profileData)
    setRounds(roundsData || [])
    setFollowerCount((followersData || []).length)
    setFollowingCount((followingData || []).length)
    setLoading(false)
  }

  async function openFollowModal(type) {
    setFollowModal(type)
    setFollowModalLoading(true)
    setFollowModalUsers([])
    let data
    if (type === 'followers') {
      const { data: rows } = await supabase
        .from('follows')
        .select('profiles!follows_follower_id_fkey(id, username, full_name, avatar_emoji, avatar_url)')
        .eq('following_id', session.user.id)
      data = (rows || []).map(r => r.profiles).filter(Boolean)
    } else {
      const { data: rows } = await supabase
        .from('follows')
        .select('profiles!follows_following_id_fkey(id, username, full_name, avatar_emoji, avatar_url)')
        .eq('follower_id', session.user.id)
      data = (rows || []).map(r => r.profiles).filter(Boolean)
    }
    setFollowModalUsers(data)
    setFollowModalLoading(false)
  }

  async function toggleVisibility() {
    const newVis = profile?.visibility === 'private' ? 'public' : 'private'
    setSavingVisibility(true)
    await supabase.from('profiles').update({ visibility: newVis }).eq('id', session.user.id)
    setProfile(prev => ({ ...prev, visibility: newVis }))
    setSavingVisibility(false)
  }

  // ── COMPUTED STATS ──────────────────────────────────────────

  const totalRounds = rounds.length

  // Only compute score stats on rounds that actually have a score
  const scoredRounds = rounds.filter(r => r.score != null)
  const bestScore = scoredRounds.length > 0
    ? Math.min(...scoredRounds.map(r => r.score))
    : null

  // Course passport — unique courses played
  const courseMap = {}
  rounds.forEach(round => {
    if (!round.course_name) return
    if (!courseMap[round.course_name]) courseMap[round.course_name] = []
    courseMap[round.course_name].push(round)
  })
  const courses = Object.entries(courseMap)
    .map(([name, courseRounds]) => {
      const scoredHere = courseRounds.filter(r => r.score != null)
      return {
        name,
        timesPlayed: courseRounds.length,
        bestScore: scoredHere.length > 0 ? Math.min(...scoredHere.map(r => r.score)) : null,
        lastPlayed: courseRounds[0].created_at,
        par: courseRounds[0].par
      }
    })
    .sort((a, b) => new Date(b.lastPlayed) - new Date(a.lastPlayed))
  const totalCourses = courses.length

  // This year's stats
  const thisYear = new Date().getFullYear()
  const roundsThisYear = rounds.filter(r => new Date(r.created_at).getFullYear() === thisYear)
  const scoredThisYear = roundsThisYear.filter(r => r.score != null)
  const avgThisYear = scoredThisYear.length > 0
    ? (scoredThisYear.reduce((sum, r) => sum + r.score, 0) / scoredThisYear.length).toFixed(1)
    : null
  const coursesThisYear = [...new Set(roundsThisYear.filter(r => r.course_name).map(r => r.course_name))].length

  // Achievements — computed from round data
  const achievements = []
  if (totalRounds >= 1)  achievements.push({ icon: '⛳', name: 'First Swing', desc: 'Logged your first round' })
  if (totalRounds >= 10) achievements.push({ icon: '🔟', name: '10 Rounds Club', desc: '10 rounds logged' })
  if (totalRounds >= 25) achievements.push({ icon: '🎯', name: 'Regular Breakie', desc: '25 rounds logged' })
  if (totalRounds >= 50) achievements.push({ icon: '💼', name: 'Dedicated Golfer', desc: '50 rounds logged' })
  if (totalRounds >= 100) achievements.push({ icon: '🔱', name: 'Century Club', desc: '100 rounds logged' })
  if (totalCourses >= 5)  achievements.push({ icon: '🗺️', name: 'Course Explorer', desc: '5 different courses' })
  if (totalCourses >= 10) achievements.push({ icon: '✈️', name: 'World Traveler', desc: '10 courses played' })
  if (totalCourses >= 25) achievements.push({ icon: '🌍', name: 'Global Golfer', desc: '25 courses played' })
  if (bestScore != null && bestScore <= 99) achievements.push({ icon: '💯', name: 'Broke 100', desc: 'Shot under 100' })
  if (bestScore != null && bestScore <= 89) achievements.push({ icon: '🟢', name: 'Broke 90', desc: 'Shot under 90' })
  if (bestScore != null && bestScore <= 79) achievements.push({ icon: '💎', name: 'Sub-80 Club', desc: 'Shot under 80' })
  if (bestScore != null && bestScore <= 69) achievements.push({ icon: '🚀', name: 'Scratch Territory', desc: 'Shot under 70' })

  // Photo rounds
  const photoRounds = rounds.filter(r => r.photo_url)

  // Recent activity — last 5 rounds for the activity feed
  const recentRounds = rounds.slice(0, 5)
  const olderRounds = rounds.slice(5)

  // ── EDIT / DELETE ──────────────────────────────────────────
  function handleRoundSaved(updatedRound) {
    setRounds(prev => prev.map(r => r.id === updatedRound.id ? { ...r, ...updatedRound } : r))
    setEditingRound(null)
  }
  function handleRoundDeleted(roundId) {
    setRounds(prev => prev.filter(r => r.id !== roundId))
    setEditingRound(null)
  }

  // ── HELPERS ────────────────────────────────────────────────
  function scoreDiffLabel(score, par) {
    if (score == null) return '—'
    if (!par) return `${score}`
    const diff = score - par
    if (diff === 0) return `${score} (E)`
    if (diff > 0) return `${score} (+${diff})`
    return `${score} (${diff})`
  }
  function scoreBadgeColor(score, par) {
    if (score == null) return '#6b7c6e'
    if (!par) return '#1a7a3c'
    const diff = score - par
    if (diff < 0) return '#1a7a3c'
    if (diff === 0) return '#2980b9'
    return '#c0392b'
  }
  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    })
  }
  function formatDateShort(dateStr) {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }
  function memberSinceStr() {
    if (!profile?.created_at) return ''
    return new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  // ── RENDER ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={st.loadingScreen}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>⛳</div>
        <div style={{ color: '#6aad80', fontSize: 14, letterSpacing: 0.5 }}>Loading passport...</div>
      </div>
    )
  }

  return (
    <div style={{ ...st.page, paddingBottom: isMobile ? 72 : 0 }}>

      {/* ── LIGHTBOX ── */}
      {lightboxUrl && (
        <div style={st.lightboxOverlay} onClick={() => setLightboxUrl(null)}>
          <img src={lightboxUrl} alt="Round" style={st.lightboxImg} />
          <button style={st.lightboxClose}>✕</button>
        </div>
      )}

      {/* ── FOLLOW MODAL ── */}
      {followModal && (
        <div style={st.modalOverlay} onClick={() => setFollowModal(null)}>
          <div style={st.modalSheet} onClick={e => e.stopPropagation()}>
            <div style={st.modalHeader}>
              <div style={st.modalTitle}>{followModal === 'followers' ? 'Followers' : 'Following'}</div>
              <button style={st.modalClose} onClick={() => setFollowModal(null)}>✕</button>
            </div>
            {followModalLoading && <div style={st.modalEmpty}>Loading...</div>}
            {!followModalLoading && followModalUsers.length === 0 && (
              <div style={st.modalEmpty}>No {followModal} yet.</div>
            )}
            {followModalUsers.map(u => (
              <div key={u.id} style={st.modalRow}>
                <div style={st.modalAvatar}>
                  <AvatarImg profile={u} />
                </div>
                <div style={st.modalUserInfo}>
                  <div style={st.modalUserName}>{u.full_name || u.username || 'Golfer'}</div>
                  {u.username && <div style={st.modalUserHandle}>@{u.username}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── EDIT ROUND MODAL ── */}
      {editingRound && (
        <EditRoundModal
          round={editingRound}
          onSave={handleRoundSaved}
          onDelete={handleRoundDeleted}
          onClose={() => setEditingRound(null)}
        />
      )}

      {/* ── TOP BAR ── */}
      <div style={st.topbar}>
        <div style={st.logo}>BREAKIE</div>
        <Link to="/log" style={st.postBtn}>+ Log Round</Link>
      </div>

      <Nav />

      {/* ── HERO SECTION (Strava-style profile header) ── */}
      <div style={st.hero}>
        <div style={st.heroContent}>
          {/* Avatar — large, overlapping the banner edge */}
          <div style={st.heroAvatar}>
            <AvatarImg profile={profile} />
          </div>

          {/* Profile identity */}
          <div style={st.heroInfo}>
            <div style={st.heroName}>
              {profile?.full_name || profile?.username || 'Golfer'}
            </div>
            {profile?.username && (
              <div style={st.heroHandle}>@{profile.username}</div>
            )}
            {profile?.bio && (
              <div style={st.heroBio}>{profile.bio}</div>
            )}
            <div style={st.heroFollowRow}>
              <button style={st.heroFollowBtn} onClick={() => openFollowModal('followers')}>
                <span style={st.heroFollowNum}>{followerCount}</span>
                <span style={st.heroFollowLbl}>Followers</span>
              </button>
              <span style={st.heroFollowDot}>·</span>
              <button style={st.heroFollowBtn} onClick={() => openFollowModal('following')}>
                <span style={st.heroFollowNum}>{followingCount}</span>
                <span style={st.heroFollowLbl}>Following</span>
              </button>
            </div>
            <div style={st.heroMeta}>
              {profile?.location && (
                <span style={st.heroMetaItem}>📍 {profile.location}</span>
              )}
              <span style={st.heroMetaItem}>
                Since {memberSinceStr()}
              </span>
              {profile?.handicap != null && (
                <span style={st.hcpBadge}>HCP {profile.handicap}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS STRIP — key numbers at a glance ── */}
      <div style={st.statsStrip}>
        {[
          { n: totalRounds, l: 'Rounds' },
          { n: totalCourses, l: 'Courses' },
          { n: bestScore ?? '—', l: 'Best Score' },
        ].map(({ n, l }, i, arr) => (
          <div key={l} style={{ ...st.statCell, borderRight: i < arr.length - 1 ? '1px solid #f0f4f1' : 'none' }}>
            <div style={st.statNum}>{n}</div>
            <div style={st.statLbl}>{l}</div>
          </div>
        ))}
      </div>

      {/* ── PRIVACY TOGGLE ── */}
      <div style={st.privacyBar}>
        <div style={st.privacyInfo}>
          <div style={st.privacyLabel}>
            {profile?.visibility === 'private' ? '🔒 Private profile' : '🌐 Public profile'}
          </div>
          <div style={st.privacyDesc}>
            {profile?.visibility === 'private'
              ? 'Only your followers can see your rounds and stats'
              : 'Anyone can view your passport'}
          </div>
        </div>
        <button
          style={{ ...st.privacyToggle, opacity: savingVisibility ? 0.5 : 1 }}
          onClick={toggleVisibility}
          disabled={savingVisibility}
        >
          {profile?.visibility === 'private' ? 'Make Public' : 'Make Private'}
        </button>
      </div>

      <div style={st.body}>

        {/* ── PHOTO STRIP ── */}
        {photoRounds.length > 0 && (
          <div style={st.section}>
            <div style={st.sectionTitle}>
              Photos
              <span style={st.sectionCount}>{photoRounds.length}</span>
            </div>
            <div style={st.photoGrid}>
              {photoRounds.map(round => (
                <div
                  key={round.id}
                  style={st.photoCell}
                  onClick={() => setLightboxUrl(round.photo_url)}
                >
                  <img src={round.photo_url} alt={round.course_name} style={st.photoImg} />
                  <div style={st.photoOverlay}>
                    <div style={st.photoCourseName}>{round.course_name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── SEASON IN REVIEW — this year's snapshot ── */}
        {roundsThisYear.length > 0 && (
          <div style={st.section}>
            <div style={st.sectionTitle}>⛳ {thisYear} Season</div>
            <div style={st.seasonGrid}>
              <div style={st.seasonCard}>
                <div style={st.seasonNum}>{roundsThisYear.length}</div>
                <div style={st.seasonLbl}>Rounds</div>
              </div>
              <div style={st.seasonCard}>
                <div style={st.seasonNum}>{coursesThisYear}</div>
                <div style={st.seasonLbl}>Courses</div>
              </div>
              <div style={st.seasonCard}>
                <div style={st.seasonNum}>{avgThisYear ?? '—'}</div>
                <div style={st.seasonLbl}>Avg Score</div>
              </div>
            </div>
          </div>
        )}

        {/* ── ACHIEVEMENTS ── */}
        {achievements.length > 0 && (
          <div style={st.section}>
            <div style={st.sectionTitle}>🏅 Achievements</div>
            <div style={st.achieveGrid}>
              {achievements.map(a => (
                <div key={a.name} style={st.achieveCard}>
                  <div style={st.achieveIcon}>{a.icon}</div>
                  <div style={st.achieveName}>{a.name}</div>
                  <div style={st.achieveDesc}>{a.desc}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── RECENT ACTIVITY — Strava-style activity cards ── */}
        {rounds.length > 0 && (
          <div style={st.section}>
            <div style={st.sectionTitle}>
              Recent Activity
              <span style={st.sectionCount}>{totalRounds} rounds</span>
            </div>

            {recentRounds.map(round => {
              const badgeBg = scoreBadgeColor(round.score, round.par)
              const hasStats = round.par || round.fir != null || round.gir != null || round.putts != null

              return (
                <div key={round.id} style={st.activityCard}>
                  {/* Card header */}
                  <div style={st.activityTop}>
                    <div style={st.activityLeft}>
                      <div style={st.activityCourse}>{round.course_name || 'Round'}</div>
                      <div style={st.activityDate}>{formatDate(round.created_at)}</div>
                    </div>
                    {round.score != null && (
                      <div style={{ ...st.activityScore, background: badgeBg }}>
                        <div style={st.activityScoreNum}>{round.score}</div>
                        {round.par && (
                          <div style={st.activityScoreDiff}>
                            {round.score - round.par === 0 ? 'E'
                              : round.score - round.par > 0 ? `+${round.score - round.par}`
                              : `${round.score - round.par}`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Caption if any */}
                  {round.caption && (
                    <div style={st.activityCaption}>{round.caption}</div>
                  )}

                  {/* Photo if any */}
                  {round.photo_url && (
                    <img
                      src={round.photo_url}
                      alt={round.course_name}
                      style={st.activityPhoto}
                      onClick={() => setLightboxUrl(round.photo_url)}
                    />
                  )}

                  {/* Stats row */}
                  {hasStats && (
                    <div style={st.activityStats}>
                      {round.par && <span style={st.activityStat}>Par {round.par}</span>}
                      {round.fir != null && <span style={st.activityStat}>FIR {round.fir}</span>}
                      {round.gir != null && <span style={st.activityStat}>GIR {round.gir}</span>}
                      {round.putts != null && <span style={st.activityStat}>{round.putts} putts</span>}
                    </div>
                  )}

                  {/* Edit button */}
                  <button style={st.activityEdit} onClick={() => setEditingRound(round)}>
                    ✎ Edit Round
                  </button>
                </div>
              )
            })}

            {/* Older rounds — collapsed by default, expand on tap */}
            {olderRounds.length > 0 && (
              <>
                {showAllRounds && olderRounds.map(round => {
                  const diff = round.par && round.score != null ? round.score - round.par : null
                  const badgeBg = scoreBadgeColor(round.score, round.par)

                  return (
                    <div key={round.id} style={st.compactRoundRow}>
                      <div style={st.compactRoundInfo}>
                        <div style={st.compactCourse}>{round.course_name || 'Round'}</div>
                        <div style={st.compactDate}>{formatDateShort(round.created_at)}</div>
                      </div>
                      {round.score != null && (
                        <div
                          style={{ ...st.compactScore, background: badgeBg, cursor: 'pointer' }}
                          onClick={() => setEditingRound(round)}
                        >
                          <div style={st.compactScoreNum}>{round.score}</div>
                          {diff !== null && (
                            <div style={st.compactScoreDiff}>
                              {diff === 0 ? 'E' : diff > 0 ? `+${diff}` : `${diff}`}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}

                <button
                  style={st.showMoreBtn}
                  onClick={() => setShowAllRounds(prev => !prev)}
                >
                  {showAllRounds
                    ? '↑ Show less'
                    : `↓ Show ${olderRounds.length} more round${olderRounds.length !== 1 ? 's' : ''}`}
                </button>
              </>
            )}
          </div>
        )}

        {/* Empty state */}
        {rounds.length === 0 && (
          <div style={st.section}>
            <div style={st.emptyState}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>⛳</div>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#0e1f13' }}>
                No rounds yet
              </div>
              <div style={{ color: '#6b7c6e', fontSize: 13, marginBottom: 20 }}>
                Log your first round to start building your passport.
              </div>
              <Link to="/log" style={st.logBtn}>Log a Round →</Link>
            </div>
          </div>
        )}

        {/* ── COURSE PASSPORT GRID ── */}
        {courses.length > 0 && (
          <div style={st.section}>
            <div style={st.sectionTitle}>
              Course Passport
              <span style={st.sectionCount}>{totalCourses} stamped</span>
            </div>
            <div style={st.courseGrid}>
              {courses.map(course => (
                <Link
                  key={course.name}
                  to={`/course?name=${encodeURIComponent(course.name)}`}
                  style={{ textDecoration: 'none' }}
                >
                  <div style={st.courseCard}>
                    <div style={st.stampBadge}>{course.timesPlayed}x</div>
                    <div style={st.courseName}>{course.name}</div>
                    {course.bestScore != null && (
                      <div style={{ ...st.courseScoreBadge, background: scoreBadgeColor(course.bestScore, course.par) }}>
                        {scoreDiffLabel(course.bestScore, course.par)}
                      </div>
                    )}
                    <div style={st.courseLastPlayed}>Last played {formatDateShort(course.lastPlayed)}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* ── CONNECTED PLATFORMS ── */}
        <div style={st.section}>
          <div style={st.sectionTitle}>Connected Platforms</div>
          <div style={st.platformRow}>
            {[
              { name: 'Arccos', connected: profile?.arccos_connected },
              { name: 'TheGrint', connected: profile?.thegrint_connected },
              { name: 'Garmin', connected: false },
            ].map(p => (
              <div key={p.name} style={{
                ...st.platformChip,
                ...(p.connected ? st.platformActive : st.platformInactive)
              }}>
                {p.connected ? '✅' : '○'} {p.name}
              </div>
            ))}
          </div>
          <div style={st.platformHint}>Connect your tracking apps to auto-import rounds</div>
        </div>

      </div>
    </div>
  )
}

// ── STYLES ──────────────────────────────────────────────────────
const st = {
  page: {
    minHeight: '100vh',
    background: '#f4f7f5',
    fontFamily: "'Barlow', sans-serif"
  },
  loadingScreen: {
    minHeight: '100vh', background: '#0e1f13',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center'
  },

  // ── Top bar + nav ─────────────────────────────────────
  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10
  },
  logo: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#c8f135', letterSpacing: 2
  },
  postBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 10, padding: '8px 16px', fontWeight: 800,
    fontSize: 13, cursor: 'pointer', textDecoration: 'none',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1
  },

  // ── Hero section (Strava-inspired) ───────────────────
  hero: {
    position: 'relative',
    background: '#0e1f13',
    overflow: 'hidden'
  },
  heroContent: {
    display: 'flex', gap: 18,
    padding: '24px 20px 24px',
  },
  heroAvatar: {
    width: 88, height: 88, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #2aad56, #1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 46,
    border: '3px solid #0e1f13',
    boxShadow: '0 0 0 3px rgba(200,241,53,0.4)'
  },
  heroInfo: {
    flex: 1,
    paddingTop: 8,
  },
  heroName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: 'white',
    letterSpacing: 0.5, lineHeight: 1.1, marginBottom: 3
  },
  heroHandle: {
    fontSize: 13, color: 'rgba(255,255,255,0.45)',
    marginBottom: 6
  },
  heroBio: {
    fontSize: 13, color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.45, marginBottom: 10
  },
  heroFollowRow: {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8
  },
  heroFollowBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    display: 'flex', alignItems: 'baseline', gap: 4
  },
  heroFollowNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: 'white'
  },
  heroFollowLbl: {
    fontSize: 11, color: 'rgba(255,255,255,0.45)'
  },
  heroFollowDot: {
    fontSize: 11, color: 'rgba(255,255,255,0.25)'
  },

  heroMeta: {
    display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center'
  },
  heroMetaItem: {
    fontSize: 11, color: 'rgba(255,255,255,0.4)'
  },
  hcpBadge: {
    display: 'inline-block',
    background: 'rgba(200,241,53,0.15)',
    border: '1px solid rgba(200,241,53,0.3)',
    color: '#c8f135',
    borderRadius: 20, padding: '3px 10px',
    fontSize: 11, fontWeight: 700, letterSpacing: 0.5
  },

  // ── Privacy bar ──────────────────────────────────────
  privacyBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 16px',
    background: '#f4f7f5', borderBottom: '1px solid #e8ede9'
  },
  privacyInfo: { flex: 1 },
  privacyLabel: { fontSize: 13, fontWeight: 700, color: '#0e1f13' },
  privacyDesc: { fontSize: 11, color: '#6b7c6e', marginTop: 2 },
  privacyToggle: {
    background: '#0e1f13', color: '#c8f135', border: 'none',
    borderRadius: 8, padding: '7px 14px', fontSize: 12, fontWeight: 800,
    cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif",
    letterSpacing: 0.5, flexShrink: 0, marginLeft: 12
  },

  // ── Stats strip — white bar below hero ──────────────
  statsStrip: {
    display: 'flex', background: 'white',
    borderBottom: '1px solid #f0f4f1',
    position: 'relative', zIndex: 1
  },
  statCell: {
    flex: 1, textAlign: 'center', padding: '16px 8px'
  },
  statNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: '#0e1f13', lineHeight: 1
  },
  statLbl: {
    fontSize: 9, color: '#9aaa9d',
    textTransform: 'uppercase', letterSpacing: 1, marginTop: 4
  },

  // ── Content body ─────────────────────────────────────
  body: {
    maxWidth: 640, margin: '0 auto',
    padding: '16px 16px 60px'
  },

  // ── Section wrapper ──────────────────────────────────
  section: {
    background: 'white', borderRadius: 18,
    padding: '20px 18px', marginBottom: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  sectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: '#0e1f13',
    letterSpacing: 1, textTransform: 'uppercase',
    marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
  },
  sectionCount: {
    fontFamily: "'Barlow', sans-serif",
    fontWeight: 600, fontSize: 11, color: '#6b7c6e',
    textTransform: 'none', letterSpacing: 0
  },

  // ── Season in Review ─────────────────────────────────
  seasonGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10
  },
  seasonCard: {
    background: '#f4f7f5', borderRadius: 12,
    padding: '14px 10px', textAlign: 'center'
  },
  seasonNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: '#0e1f13', lineHeight: 1
  },
  seasonLbl: {
    fontSize: 10, color: '#6b7c6e',
    marginTop: 5, textTransform: 'uppercase', letterSpacing: 0.8
  },

  // ── Achievements ─────────────────────────────────────
  achieveGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10
  },
  achieveCard: {
    background: '#f4f7f5', borderRadius: 12,
    padding: '14px 10px', textAlign: 'center',
    border: '1px solid #e2ebe4'
  },
  achieveIcon: { fontSize: 26, marginBottom: 6 },
  achieveName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 12, color: '#0e1f13',
    letterSpacing: 0.3, marginBottom: 3, lineHeight: 1.2
  },
  achieveDesc: { fontSize: 9, color: '#6b7c6e', lineHeight: 1.3 },

  // ── Activity Cards (Strava style) ────────────────────
  activityCard: {
    background: '#f4f7f5', borderRadius: 14,
    padding: '14px', marginBottom: 10,
    border: '1px solid #e2ebe4'
  },
  activityTop: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: 10, marginBottom: 8
  },
  activityLeft: { flex: 1 },
  activityCourse: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 18, color: '#0e1f13', lineHeight: 1.1
  },
  activityDate: { fontSize: 11, color: '#6b7c6e', marginTop: 3 },
  activityScore: {
    borderRadius: 10, padding: '6px 12px',
    textAlign: 'center', flexShrink: 0
  },
  activityScoreNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 26, color: 'white', lineHeight: 1
  },
  activityScoreDiff: {
    fontSize: 10, color: 'rgba(255,255,255,0.8)',
    fontWeight: 600, marginTop: 1
  },
  activityCaption: {
    fontSize: 13, color: '#4a5e4f', lineHeight: 1.45,
    marginBottom: 10, fontStyle: 'italic'
  },
  activityPhoto: {
    width: '100%', borderRadius: 10, display: 'block',
    marginBottom: 10, cursor: 'pointer',
    maxHeight: 280, objectFit: 'cover'
  },
  activityStats: {
    display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10
  },
  activityStat: {
    background: 'white', borderRadius: 20,
    padding: '4px 10px', fontSize: 11, fontWeight: 600,
    color: '#4a5e4f', border: '1px solid #e2ebe4'
  },
  activityEdit: {
    background: 'none', border: 'none',
    color: '#6b7c6e', fontSize: 11, fontWeight: 600,
    cursor: 'pointer', padding: 0, fontFamily: "'Barlow', sans-serif"
  },

  // ── Compact older rounds (collapsed list) ────────────
  compactRoundRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 0', borderBottom: '1px solid #f0f4f1'
  },
  compactRoundInfo: { flex: 1 },
  compactCourse: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 700, fontSize: 15, color: '#0e1f13'
  },
  compactDate: { fontSize: 11, color: '#6b7c6e', marginTop: 1 },
  compactScore: {
    borderRadius: 10, padding: '4px 12px', textAlign: 'center', minWidth: 52
  },
  compactScoreNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: 'white', lineHeight: 1.1
  },
  compactScoreDiff: { fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 600 },
  showMoreBtn: {
    width: '100%', background: 'none', border: 'none',
    color: '#1a7a3c', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', padding: '12px 0 0',
    fontFamily: "'Barlow', sans-serif"
  },

  // ── Photo strip ──────────────────────────────────────
  photoGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 4, borderRadius: 12, overflow: 'hidden'
  },
  photoCell: {
    position: 'relative', aspectRatio: '1',
    overflow: 'hidden', background: '#e2ebe4', cursor: 'pointer'
  },
  photoImg: {
    width: '100%', height: '100%', objectFit: 'cover', display: 'block'
  },
  photoOverlay: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
    padding: '16px 6px 6px'
  },
  photoCourseName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 700, fontSize: 10, color: 'white', lineHeight: 1.2, letterSpacing: 0.3
  },

  // ── Course Passport grid ─────────────────────────────
  courseGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10
  },
  courseCard: {
    background: '#f4f7f5', borderRadius: 14,
    padding: '14px 12px', position: 'relative',
    border: '1.5px solid #e2ebe4'
  },
  stampBadge: {
    position: 'absolute', top: 10, right: 10,
    background: '#0e1f13', color: '#c8f135',
    borderRadius: 20, padding: '2px 8px',
    fontSize: 10, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.5
  },
  courseName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 15, color: '#0e1f13',
    lineHeight: 1.2, marginBottom: 10, paddingRight: 32
  },
  courseScoreBadge: {
    display: 'inline-block', borderRadius: 8,
    padding: '3px 10px', fontSize: 11, fontWeight: 700,
    color: 'white', marginBottom: 8,
    fontFamily: "'Barlow Condensed', sans-serif"
  },
  courseLastPlayed: { fontSize: 10, color: '#6b7c6e' },

  // ── Connected platforms ──────────────────────────────
  platformRow: { display: 'flex', gap: 8, marginBottom: 10 },
  platformChip: {
    borderRadius: 20, padding: '7px 14px',
    fontSize: 12, fontWeight: 700, letterSpacing: 0.3
  },
  platformActive: {
    background: '#e8f5ed', color: '#1a7a3c', border: '1.5px solid #2aad56'
  },
  platformInactive: {
    background: '#f4f7f5', color: '#6b7c6e', border: '1.5px solid #e2ebe4'
  },
  platformHint: { fontSize: 11, color: '#6b7c6e' },

  // ── Empty state ──────────────────────────────────────
  emptyState: { textAlign: 'center', padding: '24px 16px' },
  logBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 10, padding: '10px 20px', fontWeight: 800,
    fontSize: 13, cursor: 'pointer', textDecoration: 'none',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1,
    display: 'inline-block'
  },

  // ── Lightbox ─────────────────────────────────────────
  lightboxOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
    zIndex: 1000, display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 16
  },
  lightboxImg: {
    maxWidth: '100%', maxHeight: '90vh',
    objectFit: 'contain', borderRadius: 8
  },
  lightboxClose: {
    position: 'absolute', top: 16, right: 16,
    background: 'rgba(255,255,255,0.15)', border: 'none',
    color: 'white', borderRadius: '50%', width: 36, height: 36,
    fontSize: 16, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },

  // ── Follow modal ─────────────────────────────────────
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
  },
  modalSheet: {
    background: 'white', borderRadius: 20,
    width: '100%', maxWidth: 480, maxHeight: '70vh',
    overflowY: 'auto', paddingBottom: 24
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 20px 14px', borderBottom: '1px solid #f0f4f1',
    position: 'sticky', top: 0, background: 'white', zIndex: 1
  },
  modalTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 16, color: '#0e1f13', letterSpacing: 0.5
  },
  modalClose: {
    background: '#f4f7f5', border: 'none', borderRadius: '50%',
    width: 28, height: 28, fontSize: 12, cursor: 'pointer', color: '#6b7c6e',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  modalEmpty: { padding: '24px', textAlign: 'center', color: '#6b7c6e', fontSize: 13 },
  modalRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 20px', borderBottom: '1px solid #f0f4f1'
  },
  modalAvatar: {
    width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #2aad56, #1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 22, overflow: 'hidden'
  },
  modalUserInfo: { flex: 1 },
  modalUserName: { fontWeight: 700, fontSize: 14, color: '#0e1f13' },
  modalUserHandle: { fontSize: 12, color: '#6b7c6e', marginTop: 1 }
}
